import { usePlayer } from "@/context/PlayerContext";
import { Play, Pause, Volume2, VolumeX, Radio } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { useMemo } from "react";

const PersistentPlayer = () => {
  const { playing, muted, volume, setVolume, setMuted, toggle, nowPlaying } = usePlayer();
  const volumeValue = useMemo(() => [Math.round(volume * 100)], [volume]);

  return (
    <div
      data-testid="persistent-player"
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-white/10 backdrop-blur-xl"
      style={{ background: "rgba(10,10,10,0.88)" }}
    >
      <div className="mx-auto flex max-w-[1400px] items-center gap-4 px-4 py-3 sm:px-6">
        {/* Live pill */}
        <div className="hidden items-center gap-2 border border-[#FF3B30]/70 px-3 py-1.5 sm:flex">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FF3B30] opacity-75"></span>
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#FF3B30]"></span>
          </span>
          <span className="font-mono text-xs font-bold uppercase tracking-[0.22em] text-[#FF3B30]">On Air</span>
        </div>

        {/* Cover */}
        <div className="h-14 w-14 flex-shrink-0 overflow-hidden border border-white/10 bg-black">
          {nowPlaying.cover_url ? (
            <img src={nowPlaying.cover_url} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[#A855F7]">
              <Radio size={22} />
            </div>
          )}
        </div>

        {/* Track meta */}
        <div className="min-w-0 flex-1">
          <div
            data-testid="player-track-title"
            className="truncate font-[Unbounded,sans-serif] text-base font-bold text-[#FAFAFA]"
          >
            {nowPlaying.title}
          </div>
          <div className="truncate font-mono text-sm font-bold uppercase tracking-[0.18em] text-[#A1A1AA]">
            {nowPlaying.artist}
            {nowPlaying.show_title ? `  //  ${nowPlaying.show_title}` : ""}
          </div>
        </div>

        {/* EQ bars (only when playing) */}
        <div className="hidden items-end gap-[3px] md:flex" aria-hidden>
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className="w-[3px] bg-[#A855F7]"
              style={{
                height: playing ? `${8 + ((i * 7) % 18)}px` : "4px",
                animation: playing ? `bbm-eq 900ms ${i * 120}ms ease-in-out infinite alternate` : "none",
              }}
            />
          ))}
        </div>

        {/* Play / Pause */}
        <button
          type="button"
          onClick={toggle}
          data-testid="player-play-pause-button"
          aria-label={playing ? "Pause stream" : "Play stream"}
          className="flex h-12 w-12 items-center justify-center border border-[#A855F7] bg-[#A855F7] text-black transition-colors duration-150 hover:bg-transparent hover:text-[#A855F7]"
        >
          {playing ? <Pause size={20} strokeWidth={2.75} /> : <Play size={20} strokeWidth={2.75} />}
        </button>

        {/* Volume */}
        <div className="hidden items-center gap-2 sm:flex" style={{ width: 150 }}>
          <button
            type="button"
            data-testid="player-mute-button"
            aria-label={muted ? "Unmute" : "Mute"}
            onClick={() => setMuted(!muted)}
            className="text-[#A1A1AA] transition-colors hover:text-[#FAFAFA]"
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          <Slider
            value={volumeValue}
            onValueChange={(v) => setVolume(v[0] / 100)}
            max={100}
            step={1}
            data-testid="player-volume-slider"
          />
        </div>
      </div>

      <style>{`
        @keyframes bbm-eq {
          from { transform: scaleY(0.4); }
          to { transform: scaleY(1.4); }
        }
      `}</style>
    </div>
  );
};

export default PersistentPlayer;
